import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Button,
  CircularProgress,
  Grid,
  FormHelperText,
} from '@mui/material';
import BenefitCard from './Components/BenefitCard';
import { GstUserPanDataFetch } from '../MyBenefits/APIs/action';
import { toast } from 'react-toastify';
import PersonDetailsForm from './Components/PersonalDetails';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AddBeneficiaryAction,
  getInsuranceDetails,
  getPancardImageAction,
  getUpdatedReationAction,
  updateBeneficiaryAction,
} from './APIs/action';
import { IDLength, UserFileDict } from '../../../utils/settings';
import AgeWarningModal from './Components/AgeWarningModal';
import { FirmOwnershipType, InsuranceType } from './constants';
import FirmDetails from './Components/FirmDetails';
import { calculateAgesAndFlags } from '../../Utils/AgeCalc';
import { trackAnalyticsEvent } from '../../../utils/clevertap';
import { useSelector } from 'react-redux';
import {
  compressImage,
  getOptimalCompressionSettings,
  validateImageType,
} from '../../Utils/imageCompression';
import { validateIsAlphaNumeric, validatePersonalPAN } from '../../Utils';
import ExistingBeneficiaryCard from './Components/ExistingBeneficiaryCard';

const AddBeneficiary = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userData = useSelector(state => state.user?.userData);
  const insuranceType = location?.state?.insurance_type;
  const selectedCompanyEntity = location?.state?.selectedCompanyEntity;
  const isEdit = location?.state?.isEdit;
  const beneficiaryData = location?.state?.beneficiary_data || {};
  const navigateTo = location?.state?.navigate_to;
  const [insuranceData, setInsuranceData] = useState([]);
  const {
    benefit_card,
    selected_firm,
    owner_list,
    owner_type_text,
    relations,
    confirmation_text,
  } = insuranceData;

  const [showAgeModal, setShowAgeModal] = useState(false);
  const [relationList, setRelationList] = useState([]);
  const [loader, setLoader] = useState(false);
  const [errors, setErrors] = useState({});
  const [showData, setShowData] = useState(false);
  const [panLoader, setPanLoader] = useState(false);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [updateRelation, setUpdateRelation] = useState(false);
  const [panError, setPanError] = useState('');
  const [showAddNewForm, setShowAddNewForm] = useState(false);
  const [isImageUpdated, setIsImageUpdated] = useState(false);
  const [showExistingBeneficiaries, setShowExistingBeneficiaries] =
    useState(false);
  const [beneficiary, setBeneficiary] = useState({
    insurance_id: insuranceType,
    firm_owner_name: '',
    id_type: UserFileDict.PAN_CARD,
    id_number: '',
    id_str_base64: null,
    full_name: '',
    dob: '',
    age: '',
    phone_number: '',
    relation: '',
    confirm: false,
    file_name: null,
    firm_type: owner_type_text?.value || '',
    gender: '',
  });
  const getAllInsuranceDetails = async () => {
    setLoader(true);
    const { data, error, message } = await getInsuranceDetails(insuranceType, selectedCompanyEntity);
    setLoader(false);
    if (!error) {
      setInsuranceData(data);
    } else {
      toast.error(message);
    }
  };
  const handleChange = (field, value) => {
    setBeneficiary((prev) => {
      const updated = { ...prev, [field]: value };
      if (field === 'dob') {
        const { age } = calculateAgesAndFlags([value])[0];
        updated.age = age;
      }
      return updated;
    });
  };
  const [compressedSize, setCompressedSize] = useState(0);
  const [isCompressing, setIsCompressing] = useState(false);
  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) {
      setBeneficiary((prev) => ({
        ...prev,
        id_str_base64: null,
        file_name: null,
      }));
      setIsImageUpdated(true);
      return;
    }

    const file = files[0];
    const acceptedFileTypes = ['image/png', 'image/jpeg', 'image/heic'];
    if (!validateImageType(file, acceptedFileTypes)) {
      toast.error('Invalid file type. Please upload PNG, JPG and JPEG.');
      return;
    }

    const filename = file.name;
    setIsCompressing(true);
    try {
      const optimalSettings = await getOptimalCompressionSettings();
      const compressionOptions = {
        maxWidth: 1024,
        maxHeight: 1024,
        quality: 0.7,
        ...optimalSettings,
      };

      const result = await compressImage(file, compressionOptions);

      setBeneficiary((prev) => ({
        ...prev,
        id_str_base64: result.base64Data,
        file_name: filename,
      }));
      setCompressedSize(result.compressedSize);
    } catch (error) {
      toast.error('Failed to upload image. Try again.');
    } finally {
      setIsCompressing(false);
    }
  };
  const resetBeneficiary = () => {
    setBeneficiary((prevState) => ({
      ...prevState,
      confirm: false,
      id_number: '',
      id_str_base64: '',
      full_name: '',
      dob: '',
      age: '',
      phone_number: '',
      file_name: '',
      relation: '',
      gender: '',
    }));
  };
  const panInputRef = useRef(null);
  const validateFields = () => {
    const newErrors = {};
    if (!beneficiary.firm_owner_name)
      newErrors.firm_owner_name = 'Firm owner name is required';
    if (!beneficiary.relation) newErrors.relation = 'Relation is required';
    if (!beneficiary.gender) newErrors.gender = 'Gender is required';
    if (!beneficiary.id_number) {
      newErrors.id_number = 'PAN number is required';
    } else if (
      !validateIsAlphaNumeric(beneficiary.id_number) ||
      beneficiary.id_number.length !== IDLength.PAN
    ) {
      newErrors.id_number = 'Incorrect format added, input PAN again.';
      panInputRef.current?.focus();
    } else if (panError) {
      newErrors.id_number = panError;
      panInputRef.current?.focus();
    }

    if (!panError && !beneficiary.dob)
      newErrors.dob = 'Date of birth is required';
    if (beneficiary.age > 60) {
      setPanError('Age is greater than 60, input another PAN.');
      panInputRef.current?.focus();
      return false;
    }

    if (beneficiary.age < 18) {
      setPanError('Age is less than 18, input another PAN.');
      newErrors.age = 'Age must be greater than 18';
      panInputRef.current?.focus();
      return false;
    }
    if (!panError && !beneficiary.phone_number) {
      newErrors.phone_number = 'Phone number is required';
    } else if (beneficiary.phone_number.length !== 10) {
      newErrors.phone_number = 'Phone number must be 10 digits';
    }
    if (!beneficiary.confirm)
      newErrors.confirm = 'Please confirm the details are correct';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  function flattenBeneficiaryData(data) {
    const flat = {
      firm_owner_name: data.firm_owner_name,
      id: data.id,
    };
    data.details.forEach(item => {
      switch (item.display_name) {
        case "Name":
          flat.full_name = item.display_value;
          break;
        case "Gender":
          flat.gender = item.display_value;
          break;
        case "D.O.B":
          flat.dob = item.display_value;
          break;
        case "Mobile No.":
          flat.phone_number = item.display_value;
          break;
        case "Relation":
          flat.relation = item.display_value;
          break;
        case "PAN":
          flat.id_number = item.display_value;
          break;
        default:
          break;
      }
    });
    return flat;
  }
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateFields()) return;
    
    // Track beneficiary form submission
    trackAnalyticsEvent('BENEFICIARY_FORM_SUBMIT', {
      manufacturer_id: userData?.manufacturer_id || '',
      benefit_type: insuranceType || '',
      form_type: isEdit ? 'edit' : 'add',
      beneficiary_name: `${beneficiary.full_name}`.trim()
    }, { module: 'beneficiary' });
    
    let payload, EditPyload, original;
    if (isEdit && beneficiaryData?.id) {
      original = flattenBeneficiaryData(beneficiaryData);
      EditPyload = {
        beneficiary_id: original.id,
        id_type: beneficiary.id_type !== original.id_type ? beneficiary.id_type : null,
        file_name: isImageUpdated ? beneficiary.file_name : null,
        id_str_base64: isImageUpdated ? beneficiary.id_str_base64 : null,
        relation: beneficiary.relation !== original.relation ? beneficiary.relation : null,
        phone_no: beneficiary.phone_number !== original.phone_number ? beneficiary.phone_number : null,
        company_esi_id: selectedCompanyEntity
      };
    } else {
      payload = {
        firm_owner_name: beneficiary.firm_owner_name,
        id_type: beneficiary.id_type,
        id_number: beneficiary.id_number,
        insurance_id: insuranceType,
        id_str_base64: beneficiary.id_str_base64 || null,
        file_name: beneficiary.file_name || null,
        full_name: beneficiary.full_name || '',
        dob: beneficiary.dob,
        age: beneficiary.age,
        phone_number: beneficiary.phone_number,
        relation: beneficiary.relation,
        firm_type: owner_type_text?.value || '',
        gender: beneficiary.gender || '',
        company_esi_id: selectedCompanyEntity
      };
    }
    setSubmitLoader(true);
    let response;
    if (isEdit && beneficiaryData?.id) {
      response = await updateBeneficiaryAction(EditPyload);
    } else {
      response = await AddBeneficiaryAction(payload);
    }
    setSubmitLoader(false);
    const { error, message, data } = response;
    if (!error) {
      toast.success(message);
      if (isEdit && beneficiaryData?.id) {
        navigateTo &&
          navigate(navigateTo, {
            state: {
              insurance_type: insuranceType,
              beneficiary_id: beneficiaryData?.id,
              selectedCompanyEntity: selectedCompanyEntity
            },
          });
      } else if (insuranceType === InsuranceType.LIFE_INSURANCE) {
        navigate('/beneficiary/add-nominee', {
          state: {
            insurance_type: insuranceType,
            beneficiary_id: data.beneficiary_id,
            selectedCompanyEntity: selectedCompanyEntity
          },
        });
      } else if (insuranceType === InsuranceType.MEDICAL_INSURANCE) {
        navigate('/beneficiary/detail', {
          state: {
            insurance_type: insuranceType,
            selectedCompanyEntity: selectedCompanyEntity
          },
        });
      }
    } else {
      toast.error(message);
    }
  };
  const onPanBlur = async (panNumber) => {
    setErrors({});
    setPanError('');
    setShowData(false);

    const isValidPan =
      validateIsAlphaNumeric(panNumber) && panNumber.length === IDLength.PAN;
    if (!panNumber) {
      setPanError('PAN number is required');
      return;
    } else if (!isValidPan) {
      setPanError('Incorrect format added, input PAN again.');
      return;
    } else if (!validatePersonalPAN(panNumber)) {
      setPanError('Please enter Personal PAN');
      return;
    }
    if (isValidPan && validatePersonalPAN(panNumber)) {
      setBeneficiary((prevState) => ({
        ...prevState,
        confirm: false,
        id_str_base64: null,
        full_name: '',
        dob: '',
        age: '',
        file_name: null,
        gender: '',
      }));
    }
    setPanLoader(true);
    const { error, message, data } = await GstUserPanDataFetch(
      panNumber,
      false,
    );
    setPanLoader(false);
    if (!error) {
      setBeneficiary((prev) => ({
        ...prev,
        gender: data?.gender || '',
        full_name: data?.name || '',
        dob: data?.date_of_birth,
        age: calculateAgesAndFlags([data?.date_of_birth])[0].age,
      }));
      setShowData(true);
    } else {
      setPanError(message || 'Something went wrong while verifying PAN');
    }
  };
  const getPancardImage = async (beneficiary_id, selectedCompanyEntity) => {
    const { error, data } = await getPancardImageAction(beneficiary_id, selectedCompanyEntity);
    if (!error) {
      setBeneficiary((prev) => ({
        ...prev,
        id_str_base64: data?.pancard_image,
        file_name: data?.file_name,
      }));
    }
  };
  useEffect(() => {
    if (relations?.length) {
      setRelationList(relations);
    }
  }, [relations]);
  useEffect(() => {
    const fetchData = async () => {
      if (!isEdit || !beneficiaryData) return;
      if (isEdit) {
        setShowData(true);
      }
      if (beneficiaryData?.id && selectedCompanyEntity) {
        await getPancardImage(beneficiaryData.id, selectedCompanyEntity);
      }
      if (beneficiaryData?.details?.length) {
        const detailsMap = {};

        for (const item of beneficiaryData.details) {
          const { display_name, display_value } = item;

          if (display_name === 'Name') {
            detailsMap.full_name = display_value;
          } else if (display_name === 'D.O.B') {
            detailsMap.dob = display_value;
            detailsMap.age = calculateAgesAndFlags([display_value])[0].age;
          } else if (display_name === 'Mobile No.') {
            detailsMap.phone_number = display_value;
          } else if (display_name === 'Gender') {
            detailsMap.gender = display_value;
          } else if (display_name === 'PAN') {
            detailsMap.id_number = display_value;
          } else if (display_name === owner_type_text?.label) {
            const matchedOwner = owner_list.find(
              (owner) => owner.value === display_value,
            );
            if (matchedOwner) {
              detailsMap.firm_owner_name = matchedOwner.value;
              detailsMap.firm_owner_label = matchedOwner.label;
            }
          } else if (display_name === 'Relation') {
            const matchedRelation = relations?.find(
              (rel) => rel.label === display_value,
            );
            if (matchedRelation) {
              detailsMap.relation = matchedRelation.value;
            } else {
              if (owner_type_text?.value) {
                const updatedRelations = await callBloodRelationAPI(
                  owner_type_text?.value,
                  true,
                );
                const matchedUpdatedRelation = updatedRelations?.find(
                  (rel) => rel.value === display_value,
                );
                if (matchedUpdatedRelation) {
                  detailsMap.relation = matchedUpdatedRelation.value;
                } else {
                  detailsMap.relation = display_value;
                }
              } else {
                detailsMap.relation = display_value;
              }
            }
          }
        }

        setBeneficiary((prev) => ({
          ...prev,
          ...detailsMap,
          firm_owner_name:
          prev.firm_owner_name || beneficiaryData?.firm_owner_name,
          id_type: UserFileDict.PAN_CARD,
          confirm: true,
        }));
      }
    };
    fetchData();
  }, [isEdit, beneficiaryData, owner_type_text, relations, owner_list]);
  useEffect(() => {
    if (
      beneficiary.age &&
      beneficiary.age > 60 &&
      owner_type_text?.value !== FirmOwnershipType.PROPRIETORSHIP &&
      owner_type_text?.value !== FirmOwnershipType.HINDU_UNDIVIDED_FAMILY
    ) {
      setShowAgeModal(true);
    }
  }, [beneficiary.age, owner_type_text]);
  useEffect(() => {
    if (insuranceType) {
      getAllInsuranceDetails();
    } else {
      navigate('/beneficiary');
    }
  }, [insuranceType]);
  const callBloodRelationAPI = async (firm_type, fromEdit = false) => {
    const { error, message, data } = await getUpdatedReationAction(firm_type);
    if (!error) {
      setRelationList(data);
      setUpdateRelation(true);
      if (!fromEdit) {
        resetBeneficiary();
        handleCloseAgeModal();
      }
      if (data && data.length > 0) {
        setBeneficiary((prev) => ({
          ...prev,
          relation: data[0].value,
        }));
      }
    } else {
      setUpdateRelation(false);
      toast.error(message);
    }
  };
  const handleCloseAgeModal = () => {
    setShowAgeModal(false);
  };

  const handleAddNewClick = () => {
    setShowAddNewForm(true);
  };
  const handleChooseExistingClick = () => {
    setShowAddNewForm(false);
    setShowExistingBeneficiaries(true);
  };
  useEffect(() => {
    if (!isEdit && owner_list?.length && !beneficiary.firm_owner_name) {
      setBeneficiary((prev) => ({
        ...prev,
        firm_owner_name: owner_list[0].value,
      }));
    }
  }, [owner_list]);
  useEffect(() => {
    if (beneficiary.firm_owner_name) {
      setBeneficiary((prev) => ({
        ...prev,
        id_number: '',
        id_str_base64: null,
        file_name: null,
        full_name: '',
        dob: '',
        age: '',
        phone_number: '',
        confirm: false,
        gender: '',
      }));
    }
  }, [beneficiary.firm_owner_name]);

  return (
    <Box
      sx={{ p: { xs: 1.5, md: 3 }, borderRadius: 2 }}
    >
      {!loader ? (
        <Box sx={{ mb: { xs: 2, md: 3 } }}>
          <Grid
            container
            spacing={2}
            sx={{ alignItems: 'stretch', mb: 3 }}
          >
            <Grid
              item
              xs={12}
              sm={6}
            >
              <BenefitCard
                item={benefit_card}
                sx={{ height: '100%' }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
            >
              <Box
                sx={{
                  border: '1px solid #D9D9D9',
                  p: 2,
                  borderRadius: 3,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-evenly',
                }}
              >
                {selected_firm &&
                  selected_firm.map((detail, i) => (
                    <React.Fragment key={i}>
                      <FirmDetails detail={detail} />
                    </React.Fragment>
                  ))}
              </Box>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <CircularProgress
            size={24}
            sx={{ color: '#FFA525' }}
          />
        </Box>
      )}
      {!showExistingBeneficiaries &&  (
        <Box sx={{ p: 3, mb: 2, border: '1px solid #E0E0E0', borderRadius: 3 }}>
          {showAddNewForm || insuranceData?.existing_beneficiaries?.length === 0 || isEdit ? (
            <>
              <Grid
                container
                spacing={2}
                sx={{ mb: 3 }}
              >
                <Grid
                  item
                  xs={12}
                  sm={6}
                >
                  <Typography
                    variant='body1'
                    sx={{
                      mb: 1,
                      color: '#636363',
                      fontWeight: 600,
                      fontSize: '16px',
                      lineHeight: 1.4,
                    }}
                  >
                    {owner_type_text?.label}
                  </Typography>

                  {owner_list && owner_list.length > 1 ? (
                    <FormControl fullWidth>
                      <Select
                        value={
                          beneficiary.firm_owner_name ||
                          owner_list[0]?.value ||
                          ''
                        }
                        onChange={(e) =>
                          setBeneficiary((prev) => ({
                            ...prev,
                            firm_owner_name: e.target.value,
                          }))
                        }
                        error={!!errors.firm_owner_name}
                        size='small'
                      >
                        {owner_list.map((owner) => (
                          <MenuItem
                            key={owner.value}
                            value={owner.value}
                          >
                            {owner.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.firm_owner_name && (
                        <FormHelperText>
                          {errors.firm_owner_name}
                        </FormHelperText>
                      )}
                    </FormControl>
                  ) : (
                    <Typography
                      variant='body2'
                      sx={{
                        mt: 1,
                        fontSize: '16px',
                        color: '#333333',
                        lineHeight: '24px',
                      }}
                    >
                      {owner_list?.[0]?.label}
                    </Typography>
                  )}
                </Grid>
              </Grid>

              <Typography
                variant='body1'
                sx={{
                  fontWeight: 600,
                  fontSize: '20px',
                  mb: 3,
                  lineHeight: '1.8',
                }}
              >
                Add Beneficiary Details
              </Typography>
              <PersonDetailsForm
                person={beneficiary}
                setPerson={(field, value) => handleChange(field, value)}
                relations={relationList}
                handleUpload={handleUpload}
                onPanBlur={onPanBlur}
                onSubmit={handleSubmit}
                confirmation_text={confirmation_text}
                panLoader={panLoader}
                errors={errors}
                showData={showData}
                isEdit={isEdit}
                panError={panError}
                panInputRef={panInputRef}
              />
              <Box sx={{ display: 'flex', mt: 3 }}>
                <Button
                  variant='contained'
                  color='primary'
                  disabled={submitLoader || !!panError || panLoader}
                  onClick={handleSubmit}
                  sx={{
                    textTransform: 'none',
                    px: 3,
                    position: 'relative',
                    width: { md: 'fit-content', xs: '100%' },
                  }}
                >
                  {submitLoader ? (
                    <CircularProgress
                      size={20}
                      color='inherit'
                    />
                  ) : (
                    'Save and Continue'
                  )}
                </Button>
              </Box>
            </>
          ) : (
            <Box>
              <Typography
                variant='body1'
                sx={{
                  fontWeight: 600,
                  fontSize: '20px',
                  mb: 3,
                  lineHeight: '1.8',
                }}
              >
                Add Beneficiary Details
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Button
                  variant='outlined'
                  onClick={handleChooseExistingClick}
                  sx={{
                    textTransform: 'none',
                    width: { xs: '100%', md: '50%' },
                  }}
                >
                  Choose Existing
                </Button>
                <Typography variant='body1'>or</Typography>
                <Button
                  variant='contained'
                  color='primary'
                  onClick={handleAddNewClick}
                  sx={{
                    textTransform: 'none',
                    width: { xs: '100%', md: '50%' },
                  }}
                >
                  Add New
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}
      {showExistingBeneficiaries && !showAddNewForm && (
        <Box>
          {!loader && (
            <Grid
              container
              spacing={2}
              sx={{ alignItems: 'stretch' }}
            >
              {insuranceData?.existing_beneficiaries?.map((item, index) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={6}
                  lg={6}
                  xl={6}
                  key={index}
                  sx={{ display: 'flex' }}
                >
                  <ExistingBeneficiaryCard
                    selectedCompanyEntity={selectedCompanyEntity}
                    beneficiary={item}
                    benefitType={insuranceType}
                    owner_type_text={owner_type_text}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}
      <AgeWarningModal
        updateRelation={updateRelation}
        showAgeModal={showAgeModal}
        close={handleCloseAgeModal}
        callBloodRelationAPI={callBloodRelationAPI}
        owner_type_text={owner_type_text}
      />
    </Box>
  );
};

export default AddBeneficiary;
